import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Plus, Loader2, X, UserCheck, AlertTriangle, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

export default function SchedulingPanel() {
  const qc = useQueryClient();
  const [loc, setLoc] = useState("");
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(plusDays(30));
  const [modal, setModal] = useState(null);

  const { data: locations = [] } = useQuery({ queryKey: ["mine-locations"], queryFn: () => api.get("/admin/locations/mine").then((r) => r.data) });
  const activeLoc = loc || locations[0]?.id || "";
  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ["shifts", activeLoc, from, to],
    queryFn: () => api.get(`/admin/shifts?location_id=${activeLoc}&date_from=${from}&date_to=${to}`).then((r) => r.data),
    enabled: !!activeLoc,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["shifts"] });

  const del = async (id) => { if (!window.confirm("Delete this shift?")) return; await api.delete(`/admin/shifts/${id}`); toast.success("Shift removed"); refresh(); };

  return (
    <div data-testid="admin-scheduling-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CalendarDays size={22} className="text-[#144F75]" /> Staff Scheduling</h2>
          <p className="text-sm text-[#4A5D6B]">Create shifts and assign enhancers — availability & conflicts are highlighted.</p>
        </div>
        <button onClick={() => setModal({ location_id: activeLoc, date: todayISO(), start_time: "09:00", end_time: "13:00", role: "", enhancer_id: "" })} data-testid="new-shift-btn" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New shift</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <select value={activeLoc} onChange={(e) => setLoc(e.target.value)} data-testid="schedule-location" className="eyw-input !py-2 text-sm">
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="eyw-input !py-2 text-sm" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="eyw-input !py-2 text-sm" />
      </div>

      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : (
        <div className="space-y-2">
          {shifts.length === 0 && <p className="text-sm text-[#4A5D6B] eyw-card p-6">No shifts in this range.</p>}
          {shifts.map((s) => (
            <div key={s.id} data-testid={`shift-${s.id}`} className="eyw-card px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[#1A242B]">{fmtDate(s.date)} · {fmtTimeRange(s.start_time, s.end_time)} {s.role && <span className="text-[#8FA89B]">· {s.role}</span>}</p>
                <p className="text-xs text-[#4A5D6B] flex items-center gap-1.5 mt-0.5">
                  {s.enhancer_id ? <><UserCheck size={12} className="text-[#5C8A72]" /> {s.enhancer_name}</> : <span className="text-[#B5794E]">Unassigned</span>}
                  {s.clock_in && <span className="text-[#144F75] inline-flex items-center gap-1"><Clock size={11} /> {s.actual_hours ? `${s.actual_hours}h worked` : "clocked in"}</span>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-full ${s.status === "assigned" ? "bg-[#144F75]/10 text-[#144F75]" : s.status === "completed" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#E0A458]/15 text-[#B5794E]"}`}>{s.status}</span>
                <button onClick={() => setModal(s)} data-testid={`edit-shift-${s.id}`} className="text-xs text-[#144F75] hover:underline">Assign / edit</button>
                <button onClick={() => del(s.id)} className="text-red-500 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && <ShiftModal shift={modal} locations={locations} onClose={() => setModal(null)} onSaved={refresh} />}
    </div>
  );
}

function ShiftModal({ shift, locations, onClose, onSaved }) {
  const editing = !!shift.id;
  const [f, setF] = useState({
    location_id: shift.location_id, date: shift.date, start_time: shift.start_time,
    end_time: shift.end_time, role: shift.role || "", enhancer_id: shift.enhancer_id || "", notes: shift.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const { data: avail = [] } = useQuery({
    queryKey: ["shift-avail", shift.id],
    queryFn: () => api.get(`/admin/shifts/${shift.id}/available-enhancers`).then((r) => r.data),
    enabled: editing,
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/shifts/${shift.id}`, f);
      else await api.post("/admin/shifts", f);
      toast.success(editing ? "Shift updated" : "Shift created");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center px-4 bg-black/40 backdrop-blur-sm py-8 overflow-y-auto" data-testid="shift-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-7 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-4">{editing ? "Assign / edit shift" : "New shift"}</h3>
        <div className="space-y-3">
          <select value={f.location_id} onChange={(e) => setF({ ...f, location_id: e.target.value })} className="eyw-input w-full text-sm">
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="shift-date" className="eyw-input text-sm sm:col-span-3" />
            <input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className="eyw-input text-sm" />
            <input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className="eyw-input text-sm" />
            <input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="Role" data-testid="shift-role" className="eyw-input text-sm" />
          </div>
          {editing ? (
            <div>
              <label className="text-xs text-[#4A5D6B] mb-1 block">Assign enhancer (availability shown)</label>
              <select value={f.enhancer_id} onChange={(e) => setF({ ...f, enhancer_id: e.target.value })} data-testid="shift-enhancer" className="eyw-input w-full text-sm">
                <option value="">Unassigned</option>
                {avail.map((a) => (
                  <option key={a.id} value={a.id} disabled={a.conflict}>
                    {`${a.name} · ${money(a.hourly_rate)}/hr ${a.conflict ? "— CONFLICT" : a.available ? "— available" : "— not in availability"}`}
                  </option>
                ))}
              </select>
              {f.enhancer_id && avail.find((a) => a.id === f.enhancer_id && a.conflict) && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertTriangle size={12} /> This enhancer has an overlapping shift.</p>
              )}
            </div>
          ) : <p className="text-xs text-[#8FA89B]">Create the shift, then re-open it to assign an enhancer by availability.</p>}
          <button onClick={save} disabled={saving} data-testid="save-shift" className="eyw-btn w-full disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Save shift</button>
        </div>
      </div>
    </div>
  );
}
